import { FormikProps } from 'formik';
import React from 'react';
import { CustomerAuth, FormSection } from '@alinta-digital/asg';
import { Element } from 'react-scroll';
import { ErrorNotification } from '../../components/ErrorNotification';
import { authenticate } from '../../components/Identification/Identification.effects';
import { FormContainerContext } from '../../contexts/FormContainerContext';
import { ICustomerAuthFields } from '@alinta-digital/asg/components/CustomerAuth/ICustomerAuthFields';
import {
  setFormContextError,
  setFormContextCustomer,
  scrollToScrollElement,
} from '../../utils/utils';
import { DirectDebitForm } from '../../views/Form/Form';
import { ButtonV2 } from '../../Common.Styled';

export interface IdentificationParentForm {
  identifyYourAccountForm: ICustomerAuthFields;
}

interface IdentificationSubFormProps {
  formikProps: FormikProps<DirectDebitForm>;
  isEdit: boolean;
  onNextClick: () => void;
}

let submitCustAuth: () => void;

export const IdentificationSubForm = ({
  formikProps,
  onNextClick,
}: IdentificationSubFormProps) => {
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [disableSubmit, setDisableSubmit] = React.useState(true);

  const {
    values: formContextValues,
    dispatch: formContextDispatch,
  } = React.useContext(FormContainerContext);
  const formContextError = formContextValues.error;

  React.useEffect(() => {
    if (formContextError.steps[0].status) {
      scrollToScrollElement('notificationError');
    } else {
      scrollToScrollElement('identificationStart');
    }
  }, [formContextError.steps]);

  return (
    <FormSection
      id="Identification"
      heading="Step 1: Your account"
      stepNumber="1"
      totalSteps="4"
      nextButton={
        !formContextError.steps[0].status ? (
          <ButtonV2
            id="identificationNextButton"
            data-testid="identification_nextButton"
            loading={submitLoading}
            disabled={disableSubmit}
            type="submit"
            primary={true}
            onClick={async (event: any) => {
              event.preventDefault();
              submitCustAuth();
            }}
          >
            Next
          </ButtonV2>
        ) : (
          undefined
        )
      }
    >
      {formContextError.steps[0].status ? (
        <ErrorNotification
          errorType={formContextError.steps[0].type}
          onClick={() => {
            setFormContextError(
              formContextValues,
              formContextDispatch,
              0,
              false,
              undefined
            );
          }}
          stateShortName={formContextValues.stateShortName}
        />
      ) : (
        <>
          <Element name="identificationStart" id="identificationStart" />
          <CustomerAuth<DirectDebitForm, IdentificationParentForm>
            accountType="fuel"
            formikProps={formikProps}
            subFormConfig={(values, validationConfig) => ({
              identifyYourAccountForm: {
                accountNumber: {
                  initialValue: values.identifyYourAccountForm.accountNumber,
                  validation:
                    validationConfig.identifyYourAccountForm.accountNumber,
                },
                firstName: {
                  initialValue: values.identifyYourAccountForm.firstName,
                  validation:
                    validationConfig.identifyYourAccountForm.firstName,
                },
                lastName: {
                  initialValue: values.identifyYourAccountForm.lastName,
                  validation: validationConfig.identifyYourAccountForm.lastName,
                },
                dob: {
                  initialValue: values.identifyYourAccountForm.dob,
                  validation: validationConfig.identifyYourAccountForm.dob,
                },
              },
            })}
            accountTooltipMsg="Your electricity reference number or gas reference number can be found on the front of your bill at the top right."
            valuesKey={'identifyYourAccountForm'}
          >
            {({ subFormFormikProps, validateSubForm }) => {
              const fieldHasErrors = Object.keys(subFormFormikProps.errors)
                .length
                ? true
                : false;
              let allFieldsPopulated = true;
              const values: { [key: string]: string } =
                subFormFormikProps.values.identifyYourAccountForm;
              const keys: string[] = Object.keys(values);
              for (const key of keys) {
                allFieldsPopulated = !values[key].length ? false : true;
              }
              if (!disableSubmit && fieldHasErrors) {
                setDisableSubmit(true);
              } else if (
                allFieldsPopulated &&
                disableSubmit &&
                !fieldHasErrors
              ) {
                setDisableSubmit(false);
              }

              submitCustAuth = async () => {
                const { identifyYourAccountForm } = subFormFormikProps.values;

                // saves value to parentForm if onEnter when user did not onBlur auth fields
                await formikProps.setValues({
                  ...formikProps.values,
                  identifyYourAccountForm: { ...identifyYourAccountForm },
                });

                setSubmitLoading(true);
                const isCustAuthFormValid = (await validateSubForm()).isValid;

                if (!isCustAuthFormValid) {
                  setSubmitLoading(false);
                  return false;
                }

                const dob = identifyYourAccountForm.dob.split('/');
                try {
                  const result = await authenticate(
                    formContextValues.stateShortName,
                    identifyYourAccountForm.accountNumber,
                    identifyYourAccountForm.firstName,
                    identifyYourAccountForm.lastName,
                    `${dob[2]}-${dob[1]}-${dob[0]}`
                  );

                  setFormContextCustomer(
                    formContextValues,
                    formContextDispatch,
                    '',
                    result.data.authenticationToken,
                    result.data.fuelSites
                  );
                  setSubmitLoading(false);

                  formikProps.setValues({
                    ...formikProps.values,
                    ...subFormFormikProps.values,
                  });

                  onNextClick();
                } catch (error) {
                  setSubmitLoading(false);
                  const data =
                    (error as any).response && (error as any).response.data
                      ? (error as any).response.data
                      : { isLockedOut: false, message: '' };

                  setFormContextError(
                    formContextValues,
                    formContextDispatch,
                    0,
                    true,
                    data.isLockedOut ? 'Locked Out' : data.message
                  );
                }
              };
              return true;
            }}
          </CustomerAuth>
        </>
      )}
    </FormSection>
  );
};
